import type React from "react"
import { createContext, useContext, useReducer, type ReactNode } from "react"
import type { Template, TemplateComponent, Size } from "../app/@types/template"
import { MAX_CANVAS_COMPONENTS } from "../common/constants"

interface TemplateState {
  template: Template
  selectedComponent: string | null
  selectedComponents: string[]
  isDragging: boolean
  gridSize: number
}

export const TemplateActions = {
    ADD_COMPONENT: "ADD_COMPONENT",
    UPDATE_COMPONENT: "UPDATE_COMPONENT",
    DELETE_COMPONENT: "DELETE_COMPONENT",
    SELECT_COMPONENT: "SELECT_COMPONENT",
    TOGGLE_SELECT_COMPONENT: "TOGGLE_SELECT_COMPONENT",
    UPDATE_MULTIPLE_COMPONENTS: "UPDATE_MULTIPLE_COMPONENTS",
    DUPLICATE_COMPONENTS: "DUPLICATE_COMPONENTS",
    DELETE_SELECTED_COMPONENTS: "DELETE_SELECTED_COMPONENTS",
    SELECT_ALL_COMPONENTS: "SELECT_ALL_COMPONENTS",
    SET_DRAGGING: "SET_DRAGGING",
    LOAD_TEMPLATE: "LOAD_TEMPLATE",
    UPDATE_CANVAS_SIZE: "UPDATE_CANVAS_SIZE",
    UPDATE_ORIENTATION: "UPDATE_ORIENTATION",
} as const;

type TemplateAction =
    | { type: typeof TemplateActions.ADD_COMPONENT; component: TemplateComponent }
    | { type: typeof TemplateActions.UPDATE_COMPONENT; id: string; updates: Partial<TemplateComponent> }
    | { type: typeof TemplateActions.DELETE_COMPONENT; id: string }
    | { type: typeof TemplateActions.SELECT_COMPONENT; id: string | null }
    | { type: typeof TemplateActions.TOGGLE_SELECT_COMPONENT; id: string }
    | { type: typeof TemplateActions.UPDATE_MULTIPLE_COMPONENTS; updates: { id: string; changes: Partial<TemplateComponent> }[] }
    | { type: typeof TemplateActions.DUPLICATE_COMPONENTS; ids: string[]; offset?: number }
    | { type: typeof TemplateActions.DELETE_SELECTED_COMPONENTS }
    | { type: typeof TemplateActions.SELECT_ALL_COMPONENTS }
    | { type: typeof TemplateActions.SET_DRAGGING; isDragging: boolean }
    | { type: typeof TemplateActions.LOAD_TEMPLATE; template: Template }
    | { type: typeof TemplateActions.UPDATE_CANVAS_SIZE; size: Size }
    | { type: typeof TemplateActions.UPDATE_ORIENTATION; orientation: 'Portrait' | 'Landscape' }

// const aspectRatio = 950 / 450
const newWidth = 950
const newHeight = 450

const initialState: TemplateState = {
  template: {
    id: "default",
    name: "New Template",
    components: [],
    orientation: "Landscape",
    canvasSize: { width: newWidth, height: newHeight },
  },
  selectedComponent: null,
  selectedComponents: [],
  isDragging: false,
  gridSize: 20,
}

function templateReducer(state: TemplateState, action: TemplateAction): TemplateState {
  switch (action.type) {
    case TemplateActions.ADD_COMPONENT: {
      // Enforce component limit
      if (state.template.components.length >= MAX_CANVAS_COMPONENTS) {
        return state;
      }
      // Clamp position within canvas bounds
      const canvasW = state.template.canvasSize.width;
      const canvasH = state.template.canvasSize.height;
      const clampedComp = {
        ...action.component,
        x: Math.max(0, Math.min(action.component.x, canvasW - action.component.width)),
        y: Math.max(0, Math.min(action.component.y, canvasH - action.component.height)),
      };
      return {
        ...state,
        template: {
          ...state.template,
          components: [...state.template.components, clampedComp],
        },
      }
    }
    case TemplateActions.UPDATE_COMPONENT:
      return {
        ...state,
        template: {
          ...state.template,
          components: state.template.components.map((comp) =>
            comp.id === action.id
              ? { ...comp, ...action.updates }
              : comp
          ) as TemplateComponent[],
        },
      }
    case TemplateActions.DELETE_COMPONENT:
      return {
        ...state,
        template: {
          ...state.template,
          components: state.template.components.filter((comp) => comp.id !== action.id),
        },
        selectedComponent: state.selectedComponent === action.id ? null : state.selectedComponent,
        selectedComponents: state.selectedComponents.filter((id) => id !== action.id),
      }
    case TemplateActions.SELECT_COMPONENT:
      return {
        ...state,
        selectedComponent: action.id,
        selectedComponents: action.id ? [action.id] : [],
      }
    case TemplateActions.TOGGLE_SELECT_COMPONENT: {
      const isAlreadySelected = state.selectedComponents.includes(action.id);
      const newSelected = isAlreadySelected
        ? state.selectedComponents.filter((id) => id !== action.id)
        : [...state.selectedComponents, action.id];
      return {
        ...state,
        selectedComponents: newSelected,
        selectedComponent: newSelected.length === 1 ? newSelected[0] : (newSelected.length === 0 ? null : state.selectedComponent),
      }
    }
    case TemplateActions.UPDATE_MULTIPLE_COMPONENTS:
      return {
        ...state,
        template: {
          ...state.template,
          components: state.template.components.map((comp) => {
            const update = action.updates.find((u) => u.id === comp.id);
            return update ? { ...comp, ...update.changes } as TemplateComponent : comp;
          }),
        },
      }
    case TemplateActions.DUPLICATE_COMPONENTS: {
      const offset = action.offset ?? 20;
      const currentCount = state.template.components.length;
      const remaining = MAX_CANVAS_COMPONENTS - currentCount;

      // No room at all
      if (remaining <= 0) {
        return state;
      }

      const componentsToDuplicate = state.template.components.filter((c) => action.ids.includes(c.id));
      // Only duplicate as many as fit within the limit
      const allowedToDuplicate = componentsToDuplicate.slice(0, remaining);
      const canvasW = state.template.canvasSize.width;
      const canvasH = state.template.canvasSize.height;
      const newComponents = allowedToDuplicate.map((comp, index) => {
        const newX = Math.max(0, Math.min(comp.x + offset, canvasW - comp.width));
        const newY = Math.max(0, Math.min(comp.y + offset, canvasH - comp.height));
        return {
          ...comp,
          id: `${comp.type}-${Date.now()}-${index}`,
          x: newX,
          y: newY,
        };
      });
      const newIds = newComponents.map((c) => c.id);
      return {
        ...state,
        template: {
          ...state.template,
          components: [...state.template.components, ...newComponents],
        },
        selectedComponents: newIds,
        selectedComponent: newIds.length === 1 ? newIds[0] : null,
      }
    }
    case TemplateActions.DELETE_SELECTED_COMPONENTS:
      return {
        ...state,
        template: {
          ...state.template,
          components: state.template.components.filter((comp) => !state.selectedComponents.includes(comp.id)),
        },
        selectedComponent: null,
        selectedComponents: [],
      }
    case TemplateActions.SELECT_ALL_COMPONENTS:
      return {
        ...state,
        selectedComponents: state.template.components.map((c) => c.id),
        selectedComponent: state.template.components.length === 1 ? state.template.components[0].id : null,
      }
    case TemplateActions.SET_DRAGGING:
      return {
        ...state,
        isDragging: action.isDragging,
      }
    case TemplateActions.LOAD_TEMPLATE:
      return {
        ...state,
        template: action.template,
        selectedComponent: null,
        selectedComponents: [],
      }
    case TemplateActions.UPDATE_CANVAS_SIZE:
      return {
        ...state,
        template: {
          ...state.template,
          canvasSize: action.size,
        },
      }
    case TemplateActions.UPDATE_ORIENTATION:
      const currentSize = state.template.canvasSize;
      const newSize = action.orientation === 'Landscape' 
        ? { width: Math.max(currentSize.width, currentSize.height), height: Math.min(currentSize.width, currentSize.height) }
        : { width: Math.min(currentSize.width, currentSize.height), height: Math.max(currentSize.width, currentSize.height) };
      
      return {
        ...state,
        template: {
          ...state.template,
          orientation: action.orientation,
          canvasSize: newSize,
        },
      }
    default:
      return state
  }
}

const TemplateContext = createContext<{
  state: TemplateState
  dispatch: React.Dispatch<TemplateAction>
} | null>(null)

export function TemplateProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(templateReducer, initialState)

  return <TemplateContext.Provider value={{ state, dispatch }}>
    {children}
  </TemplateContext.Provider>
}

export function useTemplate() {
  const context = useContext(TemplateContext)
  if (!context) {
    throw new Error("useTemplate must be used within a TemplateProvider")
  }
  return context
}
